/** Field help copy — matches Super Admin Fuel Policies reference (title, description, policy key). */

const TIP = (title, desc, policyKey) => ({ title, desc, policyKey });

export const FUEL_POLICY_FIELD_TIPS = {
    name: TIP(
        'Policy name',
        'Human-readable label for this fuel policy. Shown when binding policies to assets and in policy lists.',
        'policy.name',
    ),
    tankCapacityL: TIP(
        'Tank capacity',
        'Physical fuel tank size in litres. Caps refill events; values above this are clamped.',
        'policy.tankCapacityL',
    ),
    refillDetectionThresholdL: TIP(
        'Refill threshold',
        'Minimum jump in tank level (L) before counting a refill event. Suppresses noise from sensor fluctuations.',
        'policy.refillThresholdL',
    ),
    theftDrainThresholdL: TIP(
        'Drain (theft)',
        'Drop in tank level (L) over a short window that triggers a theft incident.',
        'policy.drainThresholdL',
    ),
    spikeFilterVolumeL: TIP(
        'Spike filter',
        'Max single-tick fuel change (L) accepted; anything larger is treated as a sensor glitch and discarded.',
        'policy.spikeFilterL',
    ),
    sloshSettleDurationSec: TIP(
        'Slosh duration',
        'Moving-average window (sec) used to smooth fuel-level oscillations from in-tank slosh.',
        'policy.sloshDurationSec',
    ),
    sloshToleranceL: TIP(
        'Slosh tolerance',
        'Max allowed deviation (L) within the slosh window before flagging volatility.',
        'policy.sloshToleranceL',
    ),
    microRefillL: TIP(
        'Micro-refill',
        'Catches small parked top-ups below the main refill threshold when the new level persists. Leave blank to disable; when set, it must be greater than 0 and below the refill threshold.',
        'policy.microRefillL',
    ),
    gapCapMin: TIP(
        'Offline gap cap',
        'Maximum minutes from each offline telemetry gap counted toward engine hours, preventing connectivity blackouts from inflating runtime and idle figures. Fuel Ledger default: 15 minutes.',
        'policy.gapCapMin',
    ),
    drainSpanMin: TIP(
        'Drain window',
        'Sliding window over which a level drop is tested against the drain threshold and max burn rate. Fuel Ledger default: 20 minutes; a longer window can suit heavy trucks.',
        'policy.drainSpanMin',
    ),
    maxBurnRateLh: TIP(
        'Max burn rate',
        'Sustained fuel loss faster than this physically plausible burn rate is classified as a drain even while ignition is on. Fuel Ledger default: 40 L/h.',
        'policy.maxBurnRateLh',
    ),
    telemetryInput: TIP(
        'Level signal unit',
        'Which telemetry field drives table/equation mapping. Litre uses fuelLevelLitre (map ranges in litres). Percentage uses fuelLevelPerc as-is (map ranges in %). Both prefers fuelLevelPerc when both are sent. Voltage uses analog input (V) mapped to litres via calibration, then derives % from tank capacity.',
        'policy.telemetryInput',
    ),
    voltageRange: TIP(
        'Valid voltage range',
        'Expected min/max voltage (V) from the sender. Readings outside this range trigger a sensor fault (readings are not reported as 0%). Backend normalises device values to volts before mapping.',
        'policy.voltageRange',
    ),
    levelSource: TIP(
        'Level mapping',
        'Method used to convert raw sensor reading to display percentage. MAP TABLE uses a lookup table; MAP EQUATION uses a quadratic fit; Fuel tank capacity detection uses vehicle preset tank capacity (fuelSetup.maxTankVolumeL) with Percentage Mapping.',
        'policy.levelSource',
    ),
    idleLhr: TIP(
        'Idling Burn Rate',
        'Estimated fuel burn rate while idling (litres per hour). Used for GNSS virtual fuel when no sensor data is available.',
        'policy.gnssVirtualFuel.idleLhr',
    ),
    cityL100km: TIP(
        'City Consumption Rate',
        'Typical fuel consumption in urban driving (L/100 km) for virtual fuel modelling.',
        'policy.gnssVirtualFuel.cityL100km',
    ),
    hwyL100km: TIP(
        'Highway Consumption Rate',
        'Typical fuel consumption on highways (L/100 km) for virtual fuel modelling.',
        'policy.gnssVirtualFuel.hwyL100km',
    ),
    avgL100km: TIP(
        'Average Consumption Rate',
        'Blended average consumption (L/100 km) when city/highway split is unknown.',
        'policy.gnssVirtualFuel.avgL100km',
    ),
    penaltyPct: TIP(
        'Overspeeding Penalty',
        'Percentage penalty applied to virtual fuel estimates when driving behaviour exceeds thresholds.',
        'policy.gnssVirtualFuel.penaltyPct',
    ),
    penaltyIntervalKmh: TIP(
        'Penalty Interval',
        'Speed interval (km/h) used with penalty percentage for aggressive driving detection.',
        'policy.gnssVirtualFuel.penaltyIntervalKmh',
    ),
};

/** Dynamic validation hints appended to descriptions where ranges depend on tank capacity. */
export function fuelPolicyFieldTipsForForm(quarterHint, spikeNum) {
    const spikeLabel = Number.isFinite(spikeNum) ? `${spikeNum} L` : 'the spike filter volume';
    const quarterLabel = Number.isFinite(parseFloat(quarterHint)) ? `${quarterHint} L` : 'tank capacity / 4';

    return {
        ...FUEL_POLICY_FIELD_TIPS,
        tankCapacityL: TIP(
            FUEL_POLICY_FIELD_TIPS.tankCapacityL.title,
            `${FUEL_POLICY_FIELD_TIPS.tankCapacityL.desc} Valid range: 5–2000 L.`,
            FUEL_POLICY_FIELD_TIPS.tankCapacityL.policyKey,
        ),
        refillDetectionThresholdL: TIP(
            FUEL_POLICY_FIELD_TIPS.refillDetectionThresholdL.title,
            `${FUEL_POLICY_FIELD_TIPS.refillDetectionThresholdL.desc} Active policy: 1–${quarterLabel}, and must be ≥ ${spikeLabel}.`,
            FUEL_POLICY_FIELD_TIPS.refillDetectionThresholdL.policyKey,
        ),
        theftDrainThresholdL: TIP(
            FUEL_POLICY_FIELD_TIPS.theftDrainThresholdL.title,
            `${FUEL_POLICY_FIELD_TIPS.theftDrainThresholdL.desc} Active policy: 1–${quarterLabel}.`,
            FUEL_POLICY_FIELD_TIPS.theftDrainThresholdL.policyKey,
        ),
        spikeFilterVolumeL: TIP(
            FUEL_POLICY_FIELD_TIPS.spikeFilterVolumeL.title,
            `${FUEL_POLICY_FIELD_TIPS.spikeFilterVolumeL.desc} Valid range: 1–50 L; refill threshold must be at least this value.`,
            FUEL_POLICY_FIELD_TIPS.spikeFilterVolumeL.policyKey,
        ),
        sloshSettleDurationSec: TIP(
            FUEL_POLICY_FIELD_TIPS.sloshSettleDurationSec.title,
            `${FUEL_POLICY_FIELD_TIPS.sloshSettleDurationSec.desc} Must be greater than 0 seconds.`,
            FUEL_POLICY_FIELD_TIPS.sloshSettleDurationSec.policyKey,
        ),
        sloshToleranceL: TIP(
            FUEL_POLICY_FIELD_TIPS.sloshToleranceL.title,
            `${FUEL_POLICY_FIELD_TIPS.sloshToleranceL.desc} Valid range: 0.1–10 L.`,
            FUEL_POLICY_FIELD_TIPS.sloshToleranceL.policyKey,
        ),
        levelSource: TIP(
            FUEL_POLICY_FIELD_TIPS.levelSource.title,
            `${FUEL_POLICY_FIELD_TIPS.levelSource.desc} Choose MAP TABLE, MAP EQUATION, or Fuel tank capacity detection.`,
            FUEL_POLICY_FIELD_TIPS.levelSource.policyKey,
        ),
    };
}
